import { MachineUserSettingDto } from '../../rubblemaster_openapi3_typescript-fetch/models/MachineUserSettingDto';

/** Maximale Länge, passend zu nvarchar(200) in dbo.MachineUserSettings. */
export const MAX_EMAIL_LENGTH = 200;

/**
 * i18n-Schlüssel. Die Texte liegen in translation.json innerhalb des
 * machine-Blocks, daher der Präfix.
 */
export const FORM_HAS_ERRORS_KEY = 'machine.serviceNotificationDialogError';
export const SAVE_ERROR_KEY = 'machine.serviceNotificationSaveError';
export const EMAIL_REQUIRED_KEY = 'machine.serviceNotificationEmailRequired';
export const EMAIL_INVALID_KEY = 'machine.serviceNotificationEmailInvalid';

/**
 * Praxistaugliche E-Mail-Prüfung. Bewusst strenger als der Browser-Default von
 * <input type="email">, der zum Beispiel "a@b" durchlässt:
 *  - Local-Part ohne führenden, abschliessenden oder doppelten Punkt
 *  - Domain mit mindestens einem Punkt
 *  - TLD aus mindestens zwei Buchstaben
 */
export const EMAIL_PATTERN =
  /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/;

/** Fehlermeldungen je E-Mail-Feld. Der Wert ist ein i18n-Schlüssel. */
export interface IMachineUserSettingErrors {
  pendingOtherEmail?: string;
  urgentOtherEmail?: string;
  criticalOtherEmail?: string;
  successOtherEmail?: string;
}

/**
 * Prüft ein einzelnes E-Mail-Feld. Wird direkt am Feld aufgerufen.
 *
 * Gibt einen i18n-Schlüssel zurück, keinen übersetzten Text: useTranslation ist
 * ein Hook und darf hier nicht aufgerufen werden. Übersetzt wird an der
 * Anzeigestelle in der Komponente.
 *
 * @param email    Inhalt des Feldes.
 * @param required True, wenn die zugehörige Checkbox angehakt ist.
 */
export function validateNotificationEmail(email: string, required: boolean): string | undefined {
  // Ist die Checkbox nicht angehakt, ist das Feld deaktiviert und ohne Bedeutung.
  // Ein dort verbliebener Text darf das Speichern nicht blockieren - der Benutzer
  // könnte ihn gar nicht korrigieren. MachineUserSettingDtoToJSON verwirft den
  // Wert beim Senden.
  if (!required) {
    return undefined;
  }

  const value = email.trim();

  if (!value) {
    return EMAIL_REQUIRED_KEY;
  }

  if (!EMAIL_PATTERN.test(value)) {
    return EMAIL_INVALID_KEY;
  }

  return undefined;
}

/** Prüft alle vier Felder auf einmal - für den Speichern-Knopf. */
export function validateAllNotificationEmails(
  settings: MachineUserSettingDto
): IMachineUserSettingErrors {
  return {
    pendingOtherEmail: validateNotificationEmail(
      settings.pendingOtherEmail,
      settings.isPendingOther
    ),
    urgentOtherEmail: validateNotificationEmail(settings.urgentOtherEmail, settings.isUrgentOther),
    criticalOtherEmail: validateNotificationEmail(
      settings.criticalOtherEmail,
      settings.isCriticalOther
    ),
    successOtherEmail: validateNotificationEmail(
      settings.successOtherEmail,
      settings.isSuccessOther
    ),
  };
}

/** Vergleicht zwei Einstellungsstände inhaltlich - für die "gibt es ungespeicherte Änderungen?"-Prüfung. */
export function areMachineUserSettingsEqual(
  a: MachineUserSettingDto,
  b: MachineUserSettingDto
): boolean {
  return (
    a.isPendingSelf === b.isPendingSelf &&
    a.isPendingOther === b.isPendingOther &&
    a.pendingOtherEmail === b.pendingOtherEmail &&
    a.isUrgentSelf === b.isUrgentSelf &&
    a.isUrgentOther === b.isUrgentOther &&
    a.urgentOtherEmail === b.urgentOtherEmail &&
    a.isCriticalSelf === b.isCriticalSelf &&
    a.isCriticalOther === b.isCriticalOther &&
    a.criticalOtherEmail === b.criticalOtherEmail &&
    a.isSuccessSelf === b.isSuccessSelf &&
    a.isSuccessOther === b.isSuccessOther &&
    a.successOtherEmail === b.successOtherEmail
  );
}

export function hasServiceNotificationErrors(errors: IMachineUserSettingErrors): boolean {
  return Boolean(
    errors.pendingOtherEmail ||
      errors.urgentOtherEmail ||
      errors.criticalOtherEmail ||
      errors.successOtherEmail
  );
}

/** Voreinstellungen, falls für die Maschine noch nichts gespeichert ist. */
export function createDefaultMachineUserSetting(
    machineId: number,
    userId: number,
    userEmail: string): MachineUserSettingDto {
  return {
    machineId,
    userId,
    userEmail,

    isPendingSelf: true,
    isPendingOther: false,
    pendingOtherEmail: '',

    isUrgentSelf: true,
    isUrgentOther: false,
    urgentOtherEmail: '',

    isCriticalSelf: true,
    isCriticalOther: false,
    criticalOtherEmail: '',

    isSuccessSelf: true,
    isSuccessOther: false,
    successOtherEmail: '',
  };
}